import { HttpClient } from '@angular/common/http';
import { Injectable, computed, effect, inject, signal } from '@angular/core';
import { catchError, map, of, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { SearchResult } from '../models/searchresult.model';

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  readonly http = inject(HttpClient);
  readonly API_URL = environment.config.backendUrl;

  // Signals for reactive state management
  readonly _searchResults = signal<SearchResult[]>([]);
  searchResults = this._searchResults.asReadonly();

  readonly _loading = signal(false);
  loading = this._loading.asReadonly();

  readonly _noMoreResults = signal(false);
  noMoreResults = this._noMoreResults.asReadonly();

  readonly _activeFilters = signal<{
    type: string[];
    tags: string[];
  }>({ type: [], tags: [] });
  activeFilters = this._activeFilters.asReadonly();

  readonly _pagination = signal({ startIndex: 0, itemsPerPage: 30 });

  // Computed properties for filters and derived state
  typeFilters = computed(() => this.generateTypeFilters(this.filteredResults()));
  tagFilters = computed(() => this.generateTagFilters(this.filteredResults()));
  

  filteredResults = computed(() =>
    this.applyFilters(this._searchResults(), this._activeFilters())
  );

  constructor() {
    effect(() => {
      if (!this._loading() && this._noMoreResults()) {
        console.log('End of search results reached.');
        this._noMoreResults.set(true); // This line triggers the error
      }
    }, { allowSignalWrites: true }); // Enable signal writes within the effect
  }
  

  /**
   * Public API for searching.
   */
  search(query: string, searchArchived: boolean = false) {

    this._loading.set(true);

    this.queryElasticSearch(query, searchArchived)
      .pipe(
        map((data) => this.transformData(data)),
        catchError((err) => {
          console.error('Search failed:', err);
          this._loading.set(false);
          return of([]); // Recover gracefully with an empty result set
        })
      )
      .subscribe((results) => {
        this.handleSearchResults(results);
      });
  }

  /**
   * Reset search results and state.
   */
  clearSearch() {
    this._pagination.set({ startIndex: 0, itemsPerPage: 30 });
    this._noMoreResults.set(false);
    this._searchResults.set([]);
  }

  /**
   * Update active type filters.
   */
  toggleTypeFilter(filter: string) {
    this.updateFilters('type', filter);
  }

  /**
   * Update active tag filters.
   */
  toggleTagFilter(filter: string) {
    this.updateFilters('tags', filter);
  }

  /**
   * Reset all active filters.
   */
  resetFilters() {
    this._activeFilters.set({ type: [], tags: [] });
  }

  /**
   * Generate type filters based on current search results.
   */
  private generateTypeFilters(results: SearchResult[]) {
    const typeMap = results.reduce((acc, result) => {
      acc[result.type] = (acc[result.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    return Object.entries(typeMap).map(([type, count]) => ({
      type,
      count,
    }));
  }

  /**
   * Generate tag filters based on current search results.
   */
  private generateTagFilters(results: SearchResult[]) {
    const tagMap = results
      .flatMap((result) => result.tags || []) // Aggregate all tags
      .reduce((acc, tag) => {
        acc[tag] = (acc[tag] || 0) + 1; // Count occurrences
        return acc;
      }, {} as Record<string, number>);

    return Object.entries(tagMap).map(([name, count]) => ({ name, count }));
  }

  /**
   * Apply active filters to the search results.
   */
  private applyFilters(
    results: SearchResult[],
    filters: { type: string[]; tags: string[] }
  ) {
    let filtered = results;

    if (filters.type.length > 0) {
      filtered = filtered.filter((result) => filters.type.includes(result.type));
    }

    if (filters.tags.length > 0) {
      filtered = filtered.filter((result) => {
        const resultTags = result.tags || [];
        const matches = resultTags.some((tag) => filters.tags.includes(tag));
        return matches;
      });
    }

    console.log('Filtered Results:', filtered);
    return filtered;
  }

  /**
   * Query the ElasticSearch API.
   */
  private queryElasticSearch(query: string, searchArchived: boolean) {
    const { startIndex, itemsPerPage } = this._pagination();
    const baseQuery = {
      query: {
        bool: {
          must: [
            {
              terms: {
                _index: [
                  "*"
                ]
              }
            },
            {
              simple_query_string: {
                query,
                default_operator: 'and',
                fields: [
                  'data.title^50',
                  '*',
                ],
              },
            },
          ],
          must_not: [
            { term: { 'data.type': 'contact_point' } },
            { term: { 'data.type': 'page' } },
            { term: {'data.type': 'tag'} },
            ...(searchArchived ? [{ term: { 'data.moderation_state': 'published' } }] : [{ term: { 'data.moderation_state': 'archived' } }]),
          ],
        },
      },
      from: startIndex,
      size: itemsPerPage,
    };

    return this.http.post<any>(`${this.API_URL}/api/search`, baseQuery).pipe(
      map((response) => {
        const hits = response.hits?.hits || [];
        return hits.map((hit: any) => hit.data || null).filter((data: any) => !!data);
      }),
      catchError((err) => {
        return throwError(() => err);
      })
    );
  }

  /**
   * Transform API data into `SearchResult` objects.
   */
  private transformData(data: any[]): any[] {
    const typeMapping: Record<string, Partial<SearchResult>> = {
      // contact_point: { type: 'Contact Point', category: 'Contact' },
      news: { type: 'Notizia', category: 'Notizia' },
      information: { type: 'Informazione', category: 'Informazione' },
      event: { type: 'Evento', category: 'Evento' },
      service: { type: 'Servizio', category: 'Servizio' },
      place: { type: 'Luogo', category: 'Luogo' },
      org_unit: { type: 'Unità organizzativa', category: 'Unità organizzativa' },
      public_document: { type: 'Documento pubblico', category: 'Documento pubblico' },
      page: { type: 'Pagina', category: 'Pagina' },
      person: { type: 'Persona', category: 'Persona' },
      tag: { type: 'Tag', category: 'Tag' },
    };

    const transformedResults = data.map((item) => {
      const mapping = typeMapping[item.type];
      if (!mapping) {
        return null;
      }

      const tagsField = `${item.type}_tags`; // e.g., page_tags, event_tags
      const fieldTags = item.field_tags || [];
      const dynamicTags = item[tagsField] || [];
      const allTags = [...(Array.isArray(dynamicTags) ? dynamicTags : []), ...(Array.isArray(fieldTags) ? fieldTags : [])]
        .map((tag: any) => {
          if (typeof tag === 'object' && tag?.title) {
            return tag.title.trim(); // Ensure tag titles are strings and trimmed
          }
          if (typeof tag === 'string') {
            return tag.trim(); // Handle raw strings
          }
          return null; // Ignore invalid tags
        })
        .filter((tag) => !!tag);

      const shortDescField = `${item.type}_short_desc`;
      const desc = `${item.type}_desc`;
      const shortDescriptionField = `${item.type}_short_description`;
      const generalDescriptionField = `${item.type}_description`;
      const bodyfIeld = `${item.type}_body.value`;

      const description =
        item[shortDescField] ||
        item[shortDescriptionField] ||
        item[generalDescriptionField] ||
        item[bodyfIeld] ||
        item[desc]
      '';
      return {
        ...mapping,
        id: item.id, // Include unique identifier
        title: item.title || 'Untitled',
        description: description,
        url: item.path?.alias || '',
        tags: allTags,
      } as SearchResult;
    }).filter((result) => !!result);

    // Deduplicate results by `id`
    const uniqueResults = transformedResults.filter(
      (result: any, index, self) => self.findIndex((r: any) => r.id === result.id) === index
    );

    return uniqueResults;
  }

  /**
   * Handle new search results and update state.
   */
  private handleSearchResults(results: SearchResult[]) {
    const currentResults = this._searchResults();
    const updatedResults = [...currentResults, ...results];

    this._searchResults.set(updatedResults);
    this._pagination.update((state) => ({
      ...state,
      startIndex: updatedResults.length,
    }));
    this._noMoreResults.set(results.length < this._pagination().itemsPerPage);
    this._loading.set(false);
  }

  /**
   * Toggle active filters.
   */
  private updateFilters(filterType: 'type' | 'tags', filterValue: string) {
    if (!filterValue) {
      return; // Guard against invalid values
    }

    this._activeFilters.update((filters) => {
      const currentFilters = filters[filterType];
      const updatedFilters = currentFilters.includes(filterValue)
        ? currentFilters.filter((value) => value !== filterValue) // Remove filter
        : [...currentFilters, filterValue]; // Add filter
      return { ...filters, [filterType]: updatedFilters };
    });
  }
}
